import { isNonEmpty } from '@sgme/fp';
import classNames from 'classnames';
import type { TagItem } from 'components/shared/Tags';
import { SgTagsPicker } from 'components/shared/Tags';
import { TinySpinner } from 'components/shared/TinySpinner/TinySpinner';
import { uniq } from 'lodash-es';
import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { getKeyCode, getXoneKeyCode } from 'state/secondaryMarket/utils';
import styled from 'styled-components';

import type { CodeSearchProps } from './CodeSearch.connect';
import { CodeTypeSelector } from './CodeTypeSelector';

const StyledTagsPicker = styled(SgTagsPicker)`
  .picker-list {
    display: none;
  }

  .item {
    color: var(--bs-danger) !important;
    border-color: var(--bs-danger) !important;
    background-color: transparent !important;
    .icon {
      color: var(--bs-danger) !important;
    }
  }

  // This is a trick to make the clear all button visible
  // because for some reason sg-picker hides it with this class
  // probably due to us overriding $$type on the item since
  // this is an internal property they exploit
  .invisible {
    visibility: ${props => props.available.length && 'visible !important'};
  }

  // These rules remove the dropdown button(icon) from being displayed
  sg-picker {
    .btn:nth-child(2) {
      display: none !important;
    }
  }
`;

const TechnicalError: FC<{ className?: string }> = ({ className }) => (
  <em className={classNames('icon text-danger icon-md', className)}>error</em>
);

function splitCodes(values: string) {
  const regex = /,|;|\n/;
  if (regex.test(values)) {
    return values.split(regex).map(x => x.trim()) ?? [];
  }
  return [values.trim()];
}

function parseCodeText(values: string) {
  const codes = splitCodes(values).filter(isNonEmpty);
  return uniq(codes);
}

function usePrevious<T>(value: T) {
  const ref = useRef(value);

  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

export const CodeSearch: FC<CodeSearchProps> = ({
  codeType,
  hasSearchFailed,
  isInternalUser,
  isLoading,
  className,
  onCodeTypeChanged,
  onSearchCodes,
  onClearSearchedCodes,
  badCodes = [],
}) => {
  const id = 'code-search';
  const previousCodeType = usePrevious(codeType);
  const intl = useIntl();
  const availableItems = useMemo(() => {
    switch (codeType) {
      case 'XONE':
        return badCodes.map(c => ({ key: c, label: getXoneKeyCode(c)!, selected: true, info: true }) satisfies TagItem);
      default:
        return badCodes.map(c => ({ key: c, label: getKeyCode(c)!, selected: true, info: true }) satisfies TagItem);
    }
  }, [badCodes, codeType]);

  const removeAllBadCodes = useCallback(() => {
    if (!badCodes.length) {
      return;
    }
    onClearSearchedCodes(badCodes);
  }, [badCodes, onClearSearchedCodes]);

  const parseCodeAndSearch = useCallback(
    (value: any) => {
      removeAllBadCodes();
      onSearchCodes(codeType, parseCodeText(value));
    },
    [codeType, onSearchCodes, removeAllBadCodes],
  );

  const onInputSubmit = useCallback(
    (value: string) => {
      parseCodeAndSearch(value);
    },
    [parseCodeAndSearch],
  );

  const onValuePasted = useCallback((value: string) => parseCodeAndSearch(value), [parseCodeAndSearch]);

  useEffect(() => {
    if (previousCodeType !== codeType) {
      removeAllBadCodes();
    }
  }, [removeAllBadCodes, codeType, previousCodeType]);
  return (
    <div className={classNames('d-flex h-100 border-start product-search', className)}>
      <StyledTagsPicker
        id={id}
        singleSelect={false}
        available={availableItems}
        onRemoveItem={(tagItem: TagItem) => onClearSearchedCodes([tagItem.key])}
        onRemoveAll={removeAllBadCodes}
        keepOrder
        onValuePasted={onValuePasted}
        showNoResultLabel={false}
        placeholder={intl.formatMessage({ id: 'secondaryMarket.search.placeholder' }, { value: codeType })}
        className="flex-grow-1 overflow-hidden product-search-input"
        noClear={false}
        onInputSubmit={onInputSubmit}
      />
      <div className="d-flex align-items-center product-search-code-type-selector">
        {hasSearchFailed && <TechnicalError className="mx-2" />}
        {isLoading && <TinySpinner size={1.5} />}
        <CodeTypeSelector value={codeType} onValueChanged={onCodeTypeChanged} isInternalUser={isInternalUser} />
      </div>
    </div>
  );
};

CodeSearch.displayName = 'CodeSearch';
